// -----------------------------------------------------------------------------
// Libraries
// -----------------------------------------------------------------------------
import React from "react";
import { Card } from "antd";
import styled from "styled-components";
import dayjs from "dayjs";
import moment from "moment";

// -----------------------------------------------------------------------------
// Components and helpers
// -----------------------------------------------------------------------------
import StyledCalendarCard from "./styles";
import { ChevronLeftIcon, LiveIcon } from "components/Icons";
import { useGetDomainGymQuery, useGetGymWorkoutsQuery, useGetScheduledStreamsQuery } from "store/api/api";
import { useNavigate, useParams } from "react-router-dom";
import { daysOfWeekShort, months } from "constants/calendarStuff";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { decodeString } from "utils";
import { workoutStatuses } from "helpers/constants";
import { Flex, Row, Col, Typography, Tag, Tooltip } from "antd";
import LiveStreamIcon from "images/svgIcons/live-stream";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import { CalendarOutlined, InfoCircleOutlined } from "@ant-design/icons";

dayjs.extend(utc);
dayjs.extend(timezone);

const Button = styled.button`
	border-radius: 5px;
	border: 1px solid #e3e5e7;
	color: #787d86;
	background-color: #ffffff;
	align-items: center;
	display: flex;
	justify-content: center;
	cursor: pointer;
	padding: 7px;

	svg {
		width: 24px;
		height: 24px;
	}

	&:hover {
		background-color: #f5f6f7;
	}

	@media (max-width: 575px) {
		padding: 6px;

		svg {
			width: 18px;
			height: 18px;
		}
	}
`;

const DayButton = styled.button`
	width: 37px;
	height: 37px;
	border-radius: 50%;
	background: ${(props) => (props.selected ? "var(--primary-color)" : "transparent")};
	align-items: center;
	display: flex;
	justify-content: center;
	cursor: pointer;
	border: none;
	color: ${(props) => (props.selected ? "#ffffff" : "#8C8C8C")};
	font-size: ${(props) => (props.selected ? "14px" : "20px")};
	font-weight: ${(props) => (props.selected ? "400" : "700")};
	&:hover {
		background-color: #f5f6f7;
		color: #000;
	}
	@media (max-width: 575px) {
		width: 32px;
		height: 32px;
	}
`;

const JoinStreamButton = styled.button`
	background: var(--primary-color, "#f2994a");
	align-items: center;
	min-width: 75px;
	display: flex;
	justify-content: center;
	cursor: pointer;
	border: none;
	color: #ffffff;
	box-shadow: 0px 4px 10px #f2994a;
	border-radius: 24px;
	font-size: 16px;
	display: flex;
	justify-content: center;
	gap: 8px;
	&:hover {
		opacity: 0.8;
	}
	&:disabled {
		cursor: not-allowed;
		background: #e3e5e7;
		box-shadow: 0 0 0 0;
		color: #787d86;
		&:hover {
			opacity: 1;
		}
	}
	font-style: normal;
	font-weight: 500;
	line-height: 22px;
	padding: 4px;
	@media (max-width: 768px) {
		width: unset;
		flex: 1;
	}
`;

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const Categories = ({ categories, number }) => {
	return (
		<Flex data-id="blockLiveCategories">
			{categories.slice(0, number).map((category, index) => (
				<Tag key={index}>{category.name}</Tag>
			))}
			{categories.length > number && (
				<Tooltip
					title={categories
						.slice(number)
						.map((category) => category.name)
						.join(", ")}
				>
					<div className="tag more-tag">{`+${categories.length - number}`}</div>
				</Tooltip>
			)}
		</Flex>
	);
};
function ScheduledWorkoutCard({ workout, stream, onJoin }) {
	const { name, coach, duration, categories, equipment, music_genre, playback_id } = workout;
	const { start_time, time_zone, currently_streaming } = stream;

	const durationInMinutes = Math.ceil(duration / 60);
	const dateTime = `${dayjs
		.tz(workout?.start_time, workout?.time_zone)
		.format("MMMM D, YYYY | h:mm A z")} | ${durationInMinutes} Mins`;
	const primaryColor = usePrimaryColor();

	const Duration = ({ className }) => {
		return (
			<Flex className={`live-workout-card__duration ${className}`} style={{ width: "100%" }}>
				<Flex vertical>
					{!!currently_streaming && (
						<div className="sb-calendar-card__inner__scheduled-list__item__right__in-progress">
							<LiveIcon style={{ height: "24px" }} />
							<p> In Progress</p>
						</div>
					)}
					{(!currently_streaming || !playback_id) && dayjs.tz(start_time, time_zone).local().isAfter(dayjs()) && (
						<p>Live in {dayjs.tz(start_time, time_zone).toNow(true)}</p>
					)}
					{!currently_streaming && dayjs.tz(start_time, time_zone).local().isBefore(dayjs()) && (
						<div className="sb-calendar-card__inner__scheduled-list__item__right__in-progress">
							<LiveIcon style={{ height: "24px" }} />
							<p> {`was scheduled to start ${dayjs.tz(start_time, time_zone).local().toNow(true)} ago`}</p>
						</div>
					)}
				</Flex>
			</Flex>
		);
	};

	return (
		<Flex wrap className="live-workout-card" justify="space-between" style={{ overflow: "hidden", width: "100%" }}>
			<Flex
				align="flex-start"
				gap={8}
				className="live-workout-card__left"
				style={{ flexWrap: "nowrap", overflow: "hidden" }}
			>
				<img
					src={workout?.image_url || "https://via.placeholder.com/130x80"}
					alt="workout"
					style={{ width: 96, height: 54, borderRadius: 8, flexShrink: 0 }}
					data-id="imgLiveThumbnail"
				/>
				<Row style={{ overflow: "hidden", flex: 1 }}>
					<Col span={24}>
						{workout.currently_streaming ? (
							<Typography.Paragraph
								style={{
									color: "#fff",
									backgroundColor: primaryColor,
									fontSize: 10,
									padding: "2px 8px",
									borderRadius: 15,
									display: "inline-flex",
									alignItems: "center",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
								ellipsis
							>
								<LiveStreamIcon />
								<span style={{ marginLeft: 8 }}>Live</span>
							</Typography.Paragraph>
						) : (
							<Typography.Paragraph
								className="date-time"
								data-id="lblLiveTime"
								style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
							>
								<LiveStreamIcon />
								<span>{dateTime}</span>
							</Typography.Paragraph>
						)}
					</Col>
					<Col span={24}>
						<Typography.Paragraph
							className="workout-name"
							ellipsis
							style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
						>
							{decodeString(name)}
						</Typography.Paragraph>
					</Col>
				</Row>
			</Flex>
			<Duration className="mobile" />
			<Flex vertical align="flex-end" justify="center" className="live-workout-card__end">
				<Flex wrap justify="space-between" gap={8} align="center" className="live-workout-card__right">
					<Col style={{ display: "flex", alignItems: "center" }} data-id="sectionLiveTags">
						{workout?.music_genre && <Tag>{workout?.music_genre}</Tag>}
						<Categories categories={categories} number={1} />
					</Col>
					<Flex className="live-workout-button" align="center" gap={8}>
						<JoinStreamButton onClick={onJoin} active disabled={!currently_streaming || !playback_id}>
							<LiveStreamIcon />
							Join
						</JoinStreamButton>
					</Flex>
				</Flex>
			</Flex>
			<Duration className="desktop" />
		</Flex>
	);
}
const CalendarCard = (props) => {
	const today = dayjs();
	const navigate = useNavigate();
	const [selectedDate, setSelectedDate] = React.useState(today);
	const handleJoinClick = (workout) => {
		navigate(`../view-live-workout/${workout.id}`);
	};
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse?.data?.id;

	const workoutsResponse = useGetGymWorkoutsQuery({ gym_id: domainGymId }, { skip: !domainGymResponse.data });
	const { data: scheduledStreams } = useGetScheduledStreamsQuery(domainGymId, { skip: !domainGymResponse.data });
	const liveWorkouts =
		workoutsResponse.data?.workouts?.filter(
			(workout) =>
				workout.start_time !== null ||
				moment
					.tz(workout.start_time, "YYYY-MM-DDTHH:mm:ss", "UTC")
					.add(workout.duration, "seconds")
					.isAfter(moment.tz("UTC"))
		) || [];

	const handleSelectDay = (day) => {
		setSelectedDate(day);
	};

	const handleNextWeek = () => {
		setSelectedDate(dayjs(selectedDate).add(1, "week"));
	};

	const handlePrevWeek = () => {
		setSelectedDate(dayjs(selectedDate).subtract(1, "week"));
	};

	const streaming =
		scheduledStreams &&
		scheduledStreams
			.filter((stream) => {
				return (
					selectedDate.endOf("day").isAfter(dayjs(stream.start_time)) &&
					selectedDate.startOf("day").isBefore(dayjs(stream.start_time))
				);
			})
			.sort((a, b) => {
				const isAfter = dayjs.tz(a.start_time, a.time_zone).isAfter(dayjs.tz(b.start_time, b.time_zone));
				if (isAfter) return 1;
				else return -1;
			});

	const scheduled = streaming
		?.map((stream) => {
			const match = liveWorkouts.find(
				(workout) => workout.id === stream.workout_id && workout.status !== workoutStatuses.DRAFT
			);
			if (!match) return null;
			return { stream, match };
		})
		.filter(Boolean);
	return (
		<StyledCalendarCard className="sb-calendar-card">
			<Card className="sb-calendar-card__inner">
				<div className="sb-calendar-card__inner__header-section">
					<div className="sb-calendar-card__inner__header-section__left">
						<h1>{months[selectedDate.month()]}</h1>
						<h2>{selectedDate.year()}</h2>
					</div>
					<div className="sb-calendar-card__inner__header-section__right">
						<Button>
							<ChevronLeftIcon onClick={handlePrevWeek} />
						</Button>
						<Button>
							<ChevronLeftIcon onClick={handleNextWeek} style={{ rotate: "180deg" }} />
						</Button>
					</div>
				</div>
				<div className="sb-calendar-card__inner__week-section">
					<div className="sb-calendar-card__inner__week-section__days">
						<div className="sb-calendar-card__inner__week-section__days__day">
							<h1>{daysOfWeekShort[selectedDate.subtract(3, "day").day()]}</h1>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<h1>{daysOfWeekShort[selectedDate.subtract(2, "day").day()]}</h1>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<h1>{daysOfWeekShort[selectedDate.subtract(1, "day").day()]}</h1>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<h1>{daysOfWeekShort[selectedDate.day()]}</h1>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<h1>{daysOfWeekShort[selectedDate.add(1, "day").day()]}</h1>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<h1>{daysOfWeekShort[selectedDate.add(2, "day").day()]}</h1>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<h1>{daysOfWeekShort[selectedDate.add(3, "day").day()]}</h1>
						</div>
					</div>
					<div className="sb-calendar-card__inner__week-section__days">
						<div className="sb-calendar-card__inner__week-section__days__day">
							<DayButton onClick={() => handleSelectDay(selectedDate.subtract(3, "day"))}>
								{selectedDate.subtract(3, "day").date()}
							</DayButton>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<DayButton onClick={() => handleSelectDay(selectedDate.subtract(2, "day"))}>
								{selectedDate.subtract(2, "day").date()}
							</DayButton>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<DayButton onClick={() => handleSelectDay(selectedDate.subtract(1, "day"))}>
								{selectedDate.subtract(1, "day").date()}
							</DayButton>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<DayButton selected={true}>{selectedDate.date()}</DayButton>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<DayButton onClick={() => handleSelectDay(selectedDate.add(1, "day"))}>
								{selectedDate.add(1, "day").date()}
							</DayButton>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<DayButton onClick={() => handleSelectDay(selectedDate.add(2, "day"))}>
								{selectedDate.add(2, "day").date()}
							</DayButton>
						</div>
						<div className="sb-calendar-card__inner__week-section__days__day">
							<DayButton onClick={() => handleSelectDay(selectedDate.add(3, "day"))}>
								{selectedDate.add(3, "day").date()}
							</DayButton>
						</div>
					</div>
				</div>
				<div className="sb-calendar-card__inner__divider" />
				<div className="sb-calendar-card__inner__scheduled-header">
					<CalendarOutlined />
					<div className="sb-calendar-card__inner__scheduled-header__text-container">
						<p>Scheduled Classes</p>
					</div>
				</div>
				<div className="sb-calendar-card__inner__scheduled-header__subtitle">
					<p>{dayjs(selectedDate).format("ddd MMM D")}</p>
				</div>
				<div className="sb-calendar-card__inner__scheduled-list">
					{!scheduled?.length && (
						<Flex className="live-workout-card" gap={16} style={{ overflow: "hidden", width: "100%", padding: 24 }}>
							<InfoCircleOutlined style={{ color: "#1777FF" }} />
							<Typography.Paragraph style={{ color: "#000", fontSize: 16 }}>
								You don't have any livestreams scheduled Today.
							</Typography.Paragraph>
						</Flex>
					)}
					{scheduled?.map(({ stream, match }) => {
						return (
							<ScheduledWorkoutCard
								key={match.id}
								workout={match}
								stream={stream}
								onJoin={() => handleJoinClick(match)}
							/>
						);
					})}
				</div>
			</Card>
		</StyledCalendarCard>
	);
};

export default CalendarCard;

